// src/components/CoreCompetencyModal.js
import React, { useMemo } from "react";
import "./CoreCompetencyModal.css";

const CoreCompetencyModal = ({ details, onClose }) => {
	// Accepts either a string (e.g., "Core Competency Requirements: {...};")
	// or an already-parsed object, and returns a normalized shape:
	// { description?: string, total_required?: number, requirements?: Record<string, number> }
	const normalized = useMemo(() => {
		// 1) If it's already an object, lightly normalize and return
		if (details && typeof details === "object") {
			const n = { ...details };
			// coerce types just in case
			if (n.total_required != null) n.total_required = Number(n.total_required);
			if (n.requirements && typeof n.requirements === "object") {
				n.requirements = Object.fromEntries(
					Object.entries(n.requirements).map(([k, v]) => [k, Number(v)])
				);
			}
			return n;
		}

		// 2) If it's a string, try to extract and parse the JSON within it
		if (typeof details === "string") {
			let s = details.trim();

			// Remove any trailing semicolons
			s = s.replace(/;+\s*$/, "");

			// If there's a label/prefix, grab the first {...} block
			const objMatch = s.match(/{[\s\S]*}$/);
			if (objMatch) s = objMatch[0];

			try {
				const obj = JSON.parse(s);
				// same light normalization
				if (obj.total_required != null)
					obj.total_required = Number(obj.total_required);
				if (obj.requirements && typeof obj.requirements === "object") {
					obj.requirements = Object.fromEntries(
						Object.entries(obj.requirements).map(([k, v]) => [k, Number(v)])
					);
				}
				return obj;
			} catch {
				// fall through
			}
		}

		// 3) If parsing failed, return null so we can show the raw text
		return null;
	}, [details]);

	const LABELS = {
		Repair_min: "Repairs (minimum)",
		Troubleshoot_min: "Troubleshooting (minimum)",
		Flexible_PMs_Repairs_or_Troubleshooting:
			"Flexible (PMs, Repairs, or Troubleshooting)",
	};

	const prettifyKey = (key) => {
		if (LABELS[key]) return LABELS[key];
		const pretty = key
			.replace(/_/g, " ")
			.replace(/\bmin\b/i, "Minimum")
			.replace(/\bpm[s]?\b/i, "PMs")
			.replace(/\bwo\b/i, "Work Orders");
		return pretty.charAt(0).toUpperCase() + pretty.slice(1);
	};

	return (
		<div className="core-competency-modal">
			<div className="core-modal-content">
				<span className="core-modal-close" onClick={onClose}>
					&times;
				</span>
				<h3 className="core-modal-header">Core Competency Requirements</h3>

				<div className="core-modal-body">
					{normalized ? (
						<>
							{normalized.description && (
								<p className="core-modal-text">{normalized.description}</p>
							)}

							{typeof normalized.total_required === "number" && (
								<p className="core-modal-text">
									Total required work orders:{" "}
									<strong>{normalized.total_required}</strong>
								</p>
							)}

							{normalized.requirements && (
								<ul className="core-modal-list">
									{Object.entries(normalized.requirements).map(([k, v]) => (
										<li key={k}>
											{prettifyKey(k)}: <strong> {v}</strong>
										</li>
									))}
								</ul>
							)}
						</>
					) : (
						// Fallback: show whatever came through (string or empty)
						<p className="core-modal-text">{String(details || "")}</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default CoreCompetencyModal;
